import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Settings, BarChart3, Database } from 'lucide-react';

export const About: React.FC = () => {
    return (
        <div className="min-h-screen bg-finance-darker py-20 px-4 sm:px-6 lg:px-8">
            <Helmet>
                <title>About ForexFund AI — Quantitative Edge & Institutional Logic</title>
                <meta name="description" content="Discover how ForexFund AI synthesizes macro data, central bank policy, and institutional sentiment into actionable signals using advanced quantitative models." />
                <link rel="canonical" href="https://www.forexfundai.com/about" />
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How does the AI establish a directional bias?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "The AI Reasoning Engine establishment directional bias by weighting interest rate differentials, central bank hawkish/dovish sentiment, and macroeconomic data points like GDP and CPI."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What data sources does ForexFund AI use?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We aggregate data from primary institutional sources, including the CFTC for COT reports, central bank official statements, and the TwelveData API for real-time market pricing."
                                }
                            }
                        ]
                    }
                    `}
                </script>
            </Helmet>
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <h1 className="text-4xl text-white font-extrabold mb-4">How ForexFund AI Works</h1>
                    <p className="text-xl text-gray-400">The intersection of Quantitative Trading and Artificial Intelligence.</p>
                </div>

                <div className="space-y-12">
                    {/* Step 1 */}
                    <div className="bg-finance-card border border-gray-800 rounded-2xl p-8 flex flex-col md:flex-row gap-8 items-start">
                        <div className="p-4 bg-blue-500/10 rounded-xl text-blue-400">
                            <Database className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-3">1. Real-Time Data Aggregation</h2>
                            <p className="text-gray-400 leading-relaxed">
                                Our backend continuously monitors macroeconomic data, central bank rate probabilities, and geopolitical events.
                                We combine this with weekly CFTC Commitment of Traders (COT) reports to understand the true "Net Long/Short"
                                positioning of global Hedge Funds.
                            </p>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="bg-finance-card border border-gray-800 rounded-2xl p-8 flex flex-col md:flex-row gap-8 items-start">
                        <div className="p-4 bg-yellow-500/10 rounded-xl text-yellow-400">
                            <Settings className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-3">2. The AI Reasoning Engine</h2>
                            <p className="text-gray-400 leading-relaxed">
                                Using advanced language models, we synthesize conflicting fundamental data to establish a definitive
                                directional bias (Bullish or Bearish). The AI acts as a Chief Macro Strategist, weighting interest rate
                                differentials against sentiment.
                            </p>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="bg-finance-card border border-gray-800 rounded-2xl p-8 flex flex-col md:flex-row gap-8 items-start">
                        <div className="p-4 bg-emerald-500/10 rounded-xl text-emerald-400">
                            <BarChart3 className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-3">3. Quant & Technical Filters</h2>
                            <p className="text-gray-400 leading-relaxed">
                                A fundamental bias is not enough. Our proprietary quant engine scans the 15-minute charts using the
                                TwelveData API. We only issue a "Master Signal" when strict mathematical indicators (like RSI divergence
                                combined with an Engulfing structure) align perfectly with our macroeconomic bias.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
